import { useState } from 'react';
import { gql, useQuery, useMutation } from '@apollo/client';
import { Box, Button } from '@mui/material';
import { Title } from '../../utils/CommonStyles';
import { IAppointment, IAppointmentData } from '../../types/appointment';
import SortableTable from '../common/SortableTable';
import CustomDialog from '../common/CustomDialog';
import { useAppDispatch } from '../../app/hooks';
import { showSuccessSnackbar } from '../../features/snackbar/snackbarSlice';
import { useSelector } from 'react-redux';
import { selectUser } from '../../features/auth/authSlice';

interface IAppointmentRow {
  _id: string;
  date: string;
  project: string;
  schedule: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  confirmed: string;
  delete: JSX.Element;
}

function Appointments() {
  const storeDispatch = useAppDispatch();
  const user = useSelector(selectUser);
  const [appointments, setAppointments] = useState<IAppointmentRow[]>([]);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useQuery<IAppointmentData>(GETAPPOINTMENTS, {
    variables: { filter: { user: user._id } },
    onCompleted: (data) => {
      const appointments = data.appointmentMany.map((appointment: IAppointment) => {
        const _id = appointment._id ?? '';
        const date = new Date(appointment.date ?? '');

        return {
          _id,
          date: `${date.toLocaleString('en-US', {
            timeZone: 'America/Toronto',
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          })} EST`,
          project: typeof appointment.project === 'object' && appointment.project ? appointment.project.name : '',
          schedule: typeof appointment.schedule === 'object' && appointment.schedule ? appointment.schedule.name : '',
          firstName: appointment.purchaserInfo.firstName ?? '',
          lastName: appointment.purchaserInfo.lastName ?? '',
          email: appointment.purchaserInfo.email ?? '',
          phoneNumber: `${appointment.purchaserInfo.primaryPhone ?? ''}`,
          confirmed: appointment.confirmed ? 'yes' : 'no',
          delete: (
            <Button variant="contained" color="warning" onClick={() => handleDialog(_id)}>
              Delete
            </Button>
          ),
        };
      });

      setAppointments(appointments);
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [deleteAppointment] = useMutation(DELETEAPPOINTMENT, {
    onCompleted: () => {
      storeDispatch(showSuccessSnackbar('Appointment has been deleted!'));
      window.location.reload();
    },
    onError: (error) => {
      console.log(error, 'err');
    },
  });

  const handleCloseSuccess = () => {
    if (selectedId) {
      deleteAppointment({ variables: { _id: selectedId } });
    }
  };

  const handleDialog = (_id: string) => {
    setSelectedId(_id);
    setDialogOpen(true);
  };

  return (
    <Box sx={{ px: 3, py: 2 }}>
      <CustomDialog
        handleClose={() => setDialogOpen(false)}
        handleCloseRemove={() => setDialogOpen(false)}
        handleCloseSuccess={() => handleCloseSuccess()}
        open={dialogOpen}
        removeButton={'No'}
        successButton={'Yes'}
        dialogContent={'Are you sure you want to delete this appointment?'}
        dialogTitle={'Delete Appointment'}
      />
      <Title>Appointments</Title>
      {appointments.length > 0 ? (
        <SortableTable
          formattedHeaders={['Date', 'Project', 'Schedule', 'First Name', 'Last Name', 'Email', 'Phone Number', 'Confirmed', 'Delete']}
          headers={['date', 'project', 'schedule', 'firstName', 'lastName', 'email', 'phoneNumber', 'confirmed', 'delete']}
          rows={appointments}
        />
      ) : (
        <div>
          <em>You currently have no appointments</em>
        </div>
      )}
    </Box>
  );
}

const GETAPPOINTMENTS = gql`
  query appointmentMany($filter: FilterFindManyAppointmentInput!) {
    appointmentMany(filter: $filter) {
      _id
      project {
        name
      }
      schedule {
        name
      }
      purchaserInfo {
        firstName
        lastName
        email
        primaryPhone
      }
      confirmed
      date
    }
  }
`;

const DELETEAPPOINTMENT = gql`
  mutation appointmentRemoveById($_id: MongoID!) {
    appointmentRemoveById(_id: $_id) {
      recordId
    }
  }
`;

export default Appointments;
